/**
 * CardFrame. UI_UX section 4.
 *
 * One component renders every card in the game at three sizes:
 *   mini  (battlefield)  art, name, Attack/Health, keyword icons as badges
 *   hand                 adds cost pips, type line and rules text
 *   zoom  (side panel)   everything, plus flavor, set code and keyword links
 *
 * It takes a CardView, so a collection card, a token and a live battlefield
 * object all render through the same path. Live conditions (damage, exhausted,
 * frozen, attached upgrades, buffed or debuffed numbers) are read off the view,
 * never recomputed here.
 *
 * Interaction state is passed in rather than derived: the battlefield knows
 * what is playable, what is a valid target and what is selected, and the frame
 * only paints it.
 */

import { forwardRef, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { CardType, Keyword, Rarity } from '@/engine';
import { FactionGlyph } from '@/ui/icons/FactionGlyph';
import { KeywordIcon, KEYWORD_GLYPHS, keywordAnchor } from '@/ui/icons/keywords';
import { CardArt } from './CardArt';
import { CostPips, costLabel } from './CostPips';
import type { CardView } from './cardView';
import styles from './CardFrame.module.css';

export type CardSize = 'mini' | 'hand' | 'zoom';

export interface CardFrameProps {
  card: CardView;
  size?: CardSize;
  /** Affordable and legal to play right now: lifts with an accent underline. */
  playable?: boolean;
  /** Cannot be paid for: dims and shows `reason` as the title. */
  unaffordable?: boolean;
  reason?: string;
  /** Targeting mode: a legal target glows, everything else dims. */
  validTarget?: boolean;
  dimmed?: boolean;
  /** Chosen as an attacker, a target or a blocker. */
  selected?: boolean;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  onContextMenu?: (event: MouseEvent<HTMLDivElement>) => void;
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
  className?: string;
  /** Extra content rendered above the frame, such as a combat arrow anchor. */
  children?: ReactNode;
  /** Suppress the exhausted rotation, e.g. inside the zoom panel. */
  flat?: boolean;
  'data-testid'?: string;
}

const TYPE_LABEL: Record<CardType, string> = {
  character: 'Character',
  wallet: 'Wallet',
  transaction: 'Transaction',
  exploit: 'Exploit',
  protocol: 'Protocol',
  upgrade: 'Upgrade',
};

export const CardFrame = forwardRef<HTMLDivElement, CardFrameProps>(function CardFrame(
  {
    card,
    size = 'hand',
    playable,
    unaffordable,
    reason,
    validTarget,
    dimmed,
    selected,
    onClick,
    onContextMenu,
    onPointerEnter,
    onPointerLeave,
    className,
    children,
    flat,
    'data-testid': testId,
  },
  ref,
) {
  const classes = [
    styles.frame,
    styles[size],
    onClick ? styles.clickable : '',
    playable ? styles.playable : '',
    unaffordable ? styles.unaffordable : '',
    validTarget ? styles.validTarget : '',
    dimmed ? styles.dimmed : '',
    selected ? styles.selected : '',
    !flat && card.exhausted ? styles.exhausted : '',
    card.frozen ? styles.frozen : '',
    !flat && card.attacking ? styles.attacking : '',
    card.blocking ? styles.blocking : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={ref}
      className={classes}
      data-faction={card.faction}
      data-card-size={size}
      data-card-id={card.cardId ?? card.tokenId ?? card.key}
      data-instance-id={card.instanceId}
      data-testid={testId}
      title={reason}
      role={onClick ? 'button' : 'group'}
      tabIndex={onClick ? 0 : undefined}
      aria-label={ariaLabel(card)}
      onClick={onClick}
      onContextMenu={onContextMenu}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                (e.currentTarget as HTMLDivElement).click();
              }
            }
          : undefined
      }
    >
      <RarityStrip rarity={card.rarity} />
      {card.legendary ? <span className={styles.legendaryBorder} aria-hidden="true" /> : null}
      {card.frozen ? <span className={styles.frostRim} aria-hidden="true" /> : null}

      {size === 'mini' ? <MiniBody card={card} /> : <FullBody card={card} size={size} />}

      {card.damage > 0 ? (
        <span className={styles.damagePip} title={`${card.damage} damage marked`}>
          {card.damage}
        </span>
      ) : null}

      {card.attachments.length > 0 ? (
        <span className={styles.attachChips}>
          {card.attachments.map((a) => (
            <span
              key={a.instanceId}
              className={styles.attachChip}
              style={{ ['--chip-color' as string]: `var(--${a.faction})` }}
              title={`Upgrade: ${a.name}`}
            >
              {a.name}
            </span>
          ))}
        </span>
      ) : null}

      {children}
    </div>
  );
});

function RarityStrip({ rarity }: { rarity: Rarity | null }) {
  if (!rarity) return null;
  return <span className={styles.rarityStrip} data-rarity={rarity} aria-hidden="true" />;
}

/**
 * Mini: the battlefield read. Name across the top, keyword icons as a compact
 * strip above the stats line, Attack and Health large in the bottom-left, the
 * faction glyph bottom-right. Each band owns its own space, so a long name
 * ellipsizes without ever running into a number.
 */
function MiniBody({ card }: { card: CardView }) {
  return (
    <>
      <div className={styles.art}>
        <CardArt card={card} />
      </div>
      <span className={styles.miniScrim} aria-hidden="true" />
      <span className={styles.miniName}>{card.name}</span>
      {card.keywords.length > 0 ? (
        <span className={styles.miniBadges}>
          {card.keywords.slice(0, 4).map((k) => (
            <span key={k} className={styles.miniBadge} title={keywordTitle(k)}>
              <KeywordIcon keyword={k} size={10} />
            </span>
          ))}
        </span>
      ) : null}
      <span className={styles.miniStats}>
        {card.attack !== null ? (
          <Stats card={card} />
        ) : (
          <span className={styles.typeBadge}>{TYPE_LABEL[card.type]}</span>
        )}
        <FactionGlyph faction={card.faction} size={12} color="var(--mat)" />
      </span>
    </>
  );
}

/** Hand and zoom: the full frame top to bottom. */
function FullBody({ card, size }: { card: CardView; size: CardSize }) {
  return (
    <>
      <div className={styles.nameBar}>
        {card.legendary ? (
          <svg className={styles.crown} width={11} height={11} viewBox="0 0 24 24" aria-hidden="true">
            <path d="M3 17 6 7l6 5 6-5 3 10z" fill="currentColor" />
          </svg>
        ) : null}
        <span className={styles.name}>{card.name}</span>
        <CostPips cost={card.cost} />
      </div>

      <div className={styles.art}>
        <CardArt card={card} />
      </div>

      <div className={styles.typeLine}>
        <FactionGlyph faction={card.faction} size={11} className={styles.glyph} />
        <span className={styles.typeText}>{typeLine(card, size)}</span>
      </div>

      <div className={styles.rules}>
        {card.keywords.length > 0 ? (
          <div className={styles.keywordRow}>
            {card.keywords.map((k) => (
              <KeywordChip key={k} keyword={k} linked={size === 'zoom'} />
            ))}
          </div>
        ) : null}
        {card.text ? <div className={styles.rulesText}>{card.text}</div> : null}
        {size === 'zoom' && card.flavor ? <div className={styles.flavor}>{card.flavor}</div> : null}
      </div>

      <div className={styles.bottomBar}>
        {card.attack !== null ? (
          <Stats card={card} />
        ) : (
          <span className={styles.typeBadge}>
            {card.walletChain ? `${card.walletChain.toUpperCase()} Wallet` : TYPE_LABEL[card.type]}
          </span>
        )}
        {card.collectorNumber !== null ? (
          <span className={styles.collector}>
            {card.set} {String(card.collectorNumber).padStart(3, '0')}
          </span>
        ) : null}
      </div>
    </>
  );
}

/** Attack and Health, green when buffed above printed, red when below. */
function Stats({ card }: { card: CardView }) {
  const atkClass =
    card.baseAttack === null || card.attack === null || card.attack === card.baseAttack
      ? ''
      : card.attack > card.baseAttack
        ? styles.buffed
        : styles.debuffed;
  const hpClass =
    card.baseHealth === null || card.health === null || card.health === card.baseHealth
      ? ''
      : card.health > card.baseHealth
        ? styles.buffed
        : styles.debuffed;
  return (
    <span className={styles.stats} aria-label={`${card.attack} attack, ${card.health} health`}>
      <span className={atkClass}>{card.attack}</span>
      <span className={styles.statSlash}>/</span>
      <span className={hpClass}>{card.health}</span>
    </span>
  );
}

/**
 * A keyword badge. In zoom it is a link into HELP, which is where the tooltip
 * layer sends a player who wants the full rule.
 */
function KeywordChip({ keyword, linked }: { keyword: Keyword; linked: boolean }) {
  const glyph = KEYWORD_GLYPHS[keyword];
  const body = (
    <>
      <KeywordIcon keyword={keyword} size={10} />
      {glyph.label}
    </>
  );
  if (!linked) {
    return (
      <span className={styles.keywordChip} title={keywordTitle(keyword)}>
        {body}
      </span>
    );
  }
  return (
    <Link className={styles.keywordChip} to={keywordAnchor(keyword)} title={keywordTitle(keyword)}>
      {body}
    </Link>
  );
}

/**
 * "CHARACTER . Bot" at hand size, the full subtype list at zoom size.
 *
 * A hand frame is 140px wide: listing every subtype there guarantees an
 * ellipsis, and "CHARACTER ..." tells a player nothing. One subtype is the
 * useful half of the line, and the rest is a hover away.
 */
function typeLine(card: CardView, size: CardSize): string {
  const parts = [TYPE_LABEL[card.type]];
  const subtypes = size === 'zoom' ? card.subtypes : card.subtypes.slice(0, 1);
  if (subtypes.length > 0) parts.push(subtypes.join(' '));
  if (card.isToken) parts.push('Token');
  return parts.join(' \u00b7 ');
}

function keywordTitle(keyword: Keyword): string {
  const g = KEYWORD_GLYPHS[keyword];
  return `${g.label}. ${g.blurb}`;
}

function ariaLabel(card: CardView): string {
  const bits = [card.name, TYPE_LABEL[card.type]];
  if (card.attack !== null) bits.push(`${card.attack} attack ${card.health} health`);
  bits.push(costLabel(card.cost));
  if (card.exhausted) bits.push('exhausted');
  if (card.frozen) bits.push('frozen');
  if (card.damage > 0) bits.push(`${card.damage} damage`);
  return bits.join(', ');
}

/** A face-down card, for the opponent hand. */
export function CardBack({ size = 'hand', className }: { size?: CardSize; className?: string }) {
  return (
    <div
      className={[styles.frame, styles[size], styles.back, className].filter(Boolean).join(' ')}
      aria-label="Face-down card"
      role="img"
    >
      <FactionGlyph faction="neutral" size={22} color="var(--ink-2)" />
    </div>
  );
}
